/**
 * The full-screen "About" reader, reached by "Read more" under ABOUT on the
 * monastery page. Shows the monastery's own long-form write-up, then a
 * cleaned, theme-styled rendering of a linked Wikipedia article.
 *
 * The Wikipedia HTML is sanitised on the server (see server/src/lib/
 * wikipedia.js — only a small set of tags and attributes survive) and is
 * safe to inject here. Attribution is a CC BY-SA licence condition, not a
 * courtesy: the `WikiAttribution` block must render wherever this content is.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import { EmptyState } from '../components/ui';
import { api } from '../lib/api';

const paragraphs = (text) =>
  String(text || '')
    .split(/\n{2,}/)
    .map((s) => s.trim())
    .filter(Boolean);

export default function MonasteryAbout() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setError('');
    api
      .monasteryAbout(id, controller.signal)
      .then(setData)
      .catch((err) => err.name !== 'AbortError' && setError(err.message));
    return () => controller.abort();
  }, [id]);

  useEffect(() => {
    if (data?.monastery?.name) {
      document.title = t('monasteryAbout.pageTitle', {
        name: data.monastery.name,
        defaultValue: `About ${data.monastery.name} · GOMPA`,
      });
    }
  }, [data, t]);

  if (error) {
    return (
      <div className="shell section">
        <EmptyState
          icon="alert"
          title={t('monasteryAbout.notFound', 'We could not open this page')}
          action={
            <Link to={`/monastery/${id}`} className="btn btn--primary">
              {t('monasteryAbout.backToMonastery', 'Back to the monastery')}
            </Link>
          }
        >
          {error}
        </EmptyState>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="shell shell--prose section">
        <div className="skeleton" style={{ height: 28, width: 160, borderRadius: 6 }} />
        <div className="skeleton" style={{ height: 44, width: '70%', margin: '16px 0 28px', borderRadius: 8 }} />
        <div className="skeleton" style={{ height: 240, borderRadius: 'var(--radius-md)' }} />
      </div>
    );
  }

  const { monastery, aboutExtended, wikipedia, wikipediaUrl, wikipediaError } = data;
  const ownParas = paragraphs(aboutExtended);

  return (
    <article className="about-reader">
      <div className="about-reader__bar shell">
        <Link to={`/monastery/${monastery.slug || monastery.id}`} className="btn btn--quiet" style={{ paddingInline: 0 }}>
          <Icon name="arrowLeft" size={16} />
          {t('monasteryAbout.back', { name: monastery.name, defaultValue: `Back to ${monastery.name}` })}
        </Link>
      </div>

      <div className="shell shell--prose about-reader__body">
        <p className="eyebrow">{t('monasteryDetail.aboutTitle', 'About')}</p>
        <h1 className="display display--lg">{monastery.name}</h1>
        {monastery.tibetanName && (
          <p className="about-reader__tib tibetan" lang="bo">{monastery.tibetanName}</p>
        )}

        {ownParas.length > 0 && (
          <div className="about-reader__own">
            {ownParas.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        )}

        {wikipedia && (
          <section className="about-reader__wiki">
            <p className="eyebrow about-reader__wiki-label">
              {t('monasteryAbout.fromWikipedia', 'From Wikipedia')}
            </p>

            {wikipedia.leadImage && (
              <figure className="about-reader__lead-img">
                <img src={wikipedia.leadImage} alt={wikipedia.title} />
              </figure>
            )}

            {wikipedia.infobox?.length > 0 && (
              <dl className="about-reader__facts">
                {wikipedia.infobox.map((row, i) => (
                  <div key={i}>
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
            )}

            <div
              className="wiki-prose"
              // eslint-disable-next-line react/no-danger -- server-sanitised, see file header
              dangerouslySetInnerHTML={{ __html: wikipedia.html }}
            />

            <WikiAttribution attribution={wikipedia.attribution} t={t} />
          </section>
        )}

        {wikipediaError && wikipediaUrl && (
          <p className="subtle about-reader__wiki-error">
            {t('monasteryAbout.wikiUnavailable', "The linked Wikipedia article couldn't be loaded right now.")}{' '}
            <a href={wikipediaUrl} target="_blank" rel="noreferrer noopener">
              {t('monasteryAbout.readOnWikipedia', 'Read it on Wikipedia')}
              <Icon name="external" size={13} style={{ marginLeft: 4, verticalAlign: -2 }} />
            </a>
          </p>
        )}
      </div>
    </article>
  );
}

function WikiAttribution({ attribution, t }) {
  return (
    <aside className="wiki-attribution">
      <Icon name="info" size={16} />
      <p>
        {t('monasteryAbout.attribution', {
          article: attribution.article,
          defaultValue: `This section is drawn from the Wikipedia article “${attribution.article}”, written by Wikipedia contributors.`,
        })}{' '}
        <a href={attribution.articleUrl} target="_blank" rel="noreferrer noopener">
          {t('monasteryAbout.viewArticle', 'View the article')}
        </a>
        {' · '}
        <a href={attribution.licenseUrl} target="_blank" rel="noreferrer noopener">
          {attribution.license}
        </a>
      </p>
    </aside>
  );
}
